import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageSquare, X, Send, BedDouble, Euro, UtensilsCrossed, Sparkles } from 'lucide-react';

const HOTEL_KB = {
  habitaciones: {
    keywords: ['habitacion','suite','room','dormir','cuarto','alojamiento','precio','costar','cuesta','caro','barato','noche'],
    response: `En Maison Dorée tenemos 4 tipos de alojamiento:\n\n**Classique** — 35m², vistas ciudad — desde 290€/noche\n**Supérieure** — 48m², terraza privada — desde 390€/noche\n**Suite Dorée** — 68m², vistas panorámicas — desde 590€/noche\n**Suite Impériale** — 120m², butler service — desde 890€/noche\n\nTodas incluyen desayuno continental, acceso al spa y WiFi.\n\n¿Quieres que te ayude a reservar?`,
    cta: { text: 'Reservar ahora', url: '/reservar' }
  },
  reservar: {
    keywords: ['reservar','reserva','booking','book','disponibilidad','disponible','fecha','checkin','checkout','entrada','salida'],
    response: `Para hacer tu reserva necesito saber:\n\n¿Cuáles son tus fechas de entrada y salida?\n¿Cuántas personas sois?\n¿Tienes preferencia de habitación?\n\nPuedes reservar directamente en nuestra web o contactar por WhatsApp.`,
    cta: { text: 'Ir al motor de reservas', url: '/reservar' }
  },
  restaurante: {
    keywords: ['restaurante','comer','cenar','cena','comida','mesa','jardin','le jardin','chef','gastronomia','menu','carta'],
    response: `Le Jardin es nuestro restaurante gastronómico.\n\n**Menú degustación** — 8 platos · 185€/persona\n**Menú de temporada** — 5 platos · 95€/persona\n**A la carta** — disponible en servicio de noche\n\nHorarios:\n- Almuerzo: L-V 13:00–15:30\n- Cena: Ma-Do 20:00–22:30\n\n¿Quieres reservar mesa?`,
    cta: { text: 'Reservar mesa', url: '/restaurante' }
  },
  spa: {
    keywords: ['spa','masaje','wellness','piscina','hammam','tratamiento','relax','meditacion'],
    response: `Nuestro Spa abre de 8:00 a 22:00 todos los días.\n\n**Tratamientos signature** desde 120€ · 60 min\n**Circuito termal** (piscina, hammam, sauna) · 45€\n**Yoga & meditación** · sesiones matutinas incluidas\n\nLos huéspedes tienen acceso preferente.`
  },
  ubicacion: {
    keywords: ['donde','ubicacion','direccion','llegar','ciudad','lugar','sitio','cerca'],
    response: `Maison Dorée se encuentra en el corazón de la ciudad:\n\n- 5 min a pie del centro histórico\n- 15 min del aeropuerto en taxi\n- Metro a 2 minutos\n- Aparcamiento privado disponible\n\nOfrecemos transfer privado desde el aeropuerto (85€).`,
    cta: { text: 'Ver ubicación', url: '/contacto' }
  },
  cancelacion: {
    keywords: ['cancelar','cancelacion','reembolso','devolucion','cambio','modificar','politica'],
    response: `Nuestra política de cancelación:\n\n**Cancelación gratuita** hasta 48h antes de la llegada\n**Modificaciones** sin cargo hasta 24h antes\nCancelaciones con menos de 48h: se cobra 1 noche\n\nPara modificar, escríbenos por WhatsApp con tu nº de confirmación.`,
    cta: { text: 'Contactar por WhatsApp', url: 'https://wa.me/34600000000' }
  },
  desayuno: {
    keywords: ['desayuno','breakfast','mañana','incluido','brunch'],
    response: `El desayuno continental está incluido en todas nuestras tarifas.\n\nSe sirve en Le Jardin de 7:00 a 11:00\nDisponible en habitación sin cargo adicional\nIncluye bollería artesanal, frutas, zumos naturales y café de origen.`
  },
  default: {
    response: `Entiendo tu consulta. Para darte la respuesta más precisa, nuestro equipo estará encantado de ayudarte directamente.\n\n¿Prefieres continuar por WhatsApp?`,
    cta: { text: 'Hablar con el equipo', url: 'https://wa.me/34600000000?text=Hola,%20tengo%20una%20consulta%20sobre%20el%20hotel' }
  }
};

function detectIntent(text) {
  const normalized = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  for (const [intent, data] of Object.entries(HOTEL_KB)) {
    if (intent === 'default') continue;
    if (data.keywords && data.keywords.some(kw => normalized.includes(kw))) return data;
  }
  return HOTEL_KB.default;
}

function formatMessage(text) {
  return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>');
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const messagesRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, typing, scrollToBottom]);

  const addBotMessage = useCallback((text, cta) => {
    setMessages(prev => [...prev, { role: 'bot', text, cta }]);
  }, []);

  const openChat = () => {
    setOpen(true);
    if (!initialized) {
      setInitialized(true);
      setTimeout(() => {
        addBotMessage('Bienvenido a Maison Dorée!\n\nSoy el asistente virtual del hotel. ¿En qué puedo ayudarte hoy?', null);
      }, 500);
    }
  };

  const sendMessage = (text) => {
    const msg = (text || input).trim();
    if (!msg || typing) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setTyping(true);
    setTimeout(() => {
      const intent = detectIntent(msg);
      addBotMessage(intent.response, intent.cta || null);
      setTyping(false);
    }, 800 + Math.random() * 600);
  };

  const suggestions = [
    { icon: <BedDouble size={14} />, text: 'Quiero reservar una habitación', label: 'Reservar habitación' },
    { icon: <Euro size={14} />, text: '¿Cuánto cuestan las habitaciones?', label: 'Ver precios' },
    { icon: <UtensilsCrossed size={14} />, text: 'Quiero reservar mesa en el restaurante', label: 'Reservar mesa' },
    { icon: <Sparkles size={14} />, text: '¿Qué servicios ofrece el hotel?', label: 'Servicios' },
  ];

  return (
    <>
      <div className={`chat-trigger ${open ? 'chat-trigger--open' : ''}`} data-testid="chat-trigger">
        <button
          className="chat-trigger__btn"
          onClick={() => open ? setOpen(false) : openChat()}
          aria-label={open ? 'Cerrar chat' : 'Abrir asistente'}
          data-testid="chat-toggle-btn"
        >
          {open ? <X size={22} /> : <MessageSquare size={22} />}
        </button>
        {!open && <span className="chat-trigger__label">Asistente</span>}
      </div>

      <div className={`chatbot ${open ? 'open' : ''}`} aria-hidden={!open} data-testid="chatbot-panel">
        <div className="chatbot__header">
          <div className="chatbot__avatar">M</div>
          <div className="chatbot__info">
            <span className="chatbot__name">Maison AI</span>
            <span className="chatbot__status"><span className="chatbot__dot" />&nbsp;En línea</span>
          </div>
          <button className="chatbot__close" onClick={() => setOpen(false)} aria-label="Cerrar chat" data-testid="chat-close-btn">
            <X size={16} />
          </button>
        </div>

        <div className="chatbot__messages" ref={messagesRef} data-testid="chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`chat-msg chat-msg--${m.role}`}>
              {m.role === 'bot' ? (
                <>
                  <div className="chat-msg__bubble" dangerouslySetInnerHTML={{ __html: formatMessage(m.text) }} />
                  {m.cta && (
                    <a href={m.cta.url} className="chat-msg__cta" target={m.cta.url.startsWith('http') ? '_blank' : undefined} rel={m.cta.url.startsWith('http') ? 'noopener noreferrer' : undefined}>
                      {m.cta.text}
                    </a>
                  )}
                </>
              ) : (
                <div className="chat-msg__bubble">{m.text}</div>
              )}
            </div>
          ))}
          {typing && (
            <div className="chat-msg chat-msg--bot chat-msg--typing">
              <div className="chat-typing"><span /><span /><span /></div>
            </div>
          )}
        </div>

        {messages.length <= 1 && !typing && (
          <div className="chatbot__suggestions" data-testid="chat-suggestions">
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => sendMessage(s.text)} data-testid={`chat-suggestion-${i}`}>
                {s.icon} {s.label}
              </button>
            ))}
          </div>
        )}

        <div className="chatbot__input-area">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder="Escribe tu pregunta..."
            aria-label="Mensaje al asistente"
            data-testid="chat-input"
          />
          <button onClick={() => sendMessage()} className="chatbot__send" aria-label="Enviar" data-testid="chat-send-btn">
            <Send size={18} />
          </button>
        </div>
      </div>
    </>
  );
}
